// Geteilte UI-Primitive fuer das Plant-Maintenance-Modul.
// Touch-first: grosse Ziele (>=64px bei Aktionen), hoher Kontrast, abgerundet.
//
// Portierung aus dem Vite-Prototyp (frontend/src/components/ui.jsx): dort kamen
// Farben/Blur/Schatten aus einem Token-System (index.css + ui/theme.jsx) ohne
// Per-Komponenten-Verzweigung. Im Monolithen gibt es KEINE v4-Tokens — wir
// verzweigen explizit auf `isDarkMode` aus useTheme() und geben zwei konkrete
// Klassen-Strings aus (Glas/Transluzent fuer Dunkel, Klar/Solid fuer Hell).
// PM-Akzentfarbe ist amber, der primaere Aktionsakzent ist `brand` (Tailwind-Token).
import React from 'react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

/**
 * Card — themed Container/Oberflaeche.
 * @param {React.ReactNode} children
 * @param {string} [className] — wird an die Basisklassen angehaengt
 */
export function Card({ children, className = '' }) {
  const { isDarkMode } = useTheme();
  return (
    <div
      className={`p-4 ${isDarkMode
        ? 'rounded-3xl border border-white/10 bg-gradient-to-br from-gray-900/40 via-gray-900/20 to-gray-900/40 backdrop-blur-2xl shadow-lg'
        : 'rounded-3xl glass-light-card'
      } ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * Button — touch-first, full-width (min-h 64px, rounded-2xl).
 * @param {React.ReactNode} children
 * @param {'primary'|'confirm'|'secondary'} [variant]
 *   primary   = vorwaerts / weiter
 *   confirm   = die kritische, eindeutige Aktion (anlegen / buchen / freigeben)
 *   secondary = alternativ / verwerfen
 * @param {string} [className] — Overrides (z. B. !min-h-[48px] zum Verkleinern inline)
 * @param {object} [rest] — onClick, disabled, type, ... werden auf <button> gespreizt
 */
export function Button({ children, variant = 'primary', className = '', ...rest }) {
  const { isDarkMode } = useTheme();
  const base =
    'w-full min-h-[64px] rounded-2xl text-lg font-semibold flex items-center justify-center gap-2 px-5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100';

  // Zwei vollstaendige Klassen-Strings pro Variante (kein dark:-Variant, keine Konkatenation
  // von Farbfragmenten — Tailwind v3 purged nach literaler String-Praesenz).
  const variants = isDarkMode
    ? {
        primary:   'bg-brand/20 hover:bg-brand/30 text-blue-200 border border-brand/40',
        confirm:   'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-400/40',
        secondary: 'bg-white/10 hover:bg-white/15 text-white/80 border border-white/10',
      }
    : {
        primary:   'bg-brand hover:bg-brand-700 text-white border border-transparent',
        confirm:   'bg-emerald-600 hover:bg-emerald-700 text-white border border-transparent',
        secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200',
      };

  return (
    <button className={`${base} ${variants[variant]} ${className}`} {...rest}>
      {children}
    </button>
  );
}

/**
 * StepDots — Wizard-Fortschritt: nummerierte Punkte + Verbinder.
 * @param {number} step   — 1-basierter aktueller Schritt
 * @param {number} total  — Anzahl Schritte
 * @param {string[]} labels — Schritt-Beschriftungen (bereits uebersetzt)
 */
export function StepDots({ step, total, labels }) {
  const { isDarkMode } = useTheme();
  return (
    <div className="flex items-center gap-2 mb-5">
      {Array.from({ length: total }).map((_, i) => {
        const isDone = i + 1 < step;
        const isCurrent = i + 1 === step;
        const isReached = i + 1 <= step;

        // Zahl-Bubble: erledigt = gefuellt brand, aktuell = soft + Ring, sonst gedaempft.
        const bubble = isDone
          ? (isDarkMode ? 'bg-brand text-blue-200' : 'bg-brand text-white')
          : isCurrent
          ? (isDarkMode
              ? 'bg-brand/[0.12] text-blue-200 ring-2 ring-brand'
              : 'bg-brand/[0.08] text-brand ring-2 ring-brand')
          : (isDarkMode ? 'bg-white/[0.08] text-white/50' : 'bg-slate-100 text-slate-500');

        // Beschriftungs-Textfarbe: erreichte Schritte hervorgehoben, kommende gedaempft.
        const labelColor = isReached
          ? (isDarkMode ? 'text-blue-200' : 'text-brand')
          : (isDarkMode ? 'text-white/40' : 'text-slate-400');

        // Verbinder zwischen den Punkten.
        const connector = isDone
          ? 'bg-brand'
          : (isDarkMode ? 'bg-white/10' : 'bg-slate-200');

        return (
          <div key={i} className="flex items-center gap-2 flex-1 last:flex-none">
            <div className={`flex items-center gap-1.5 ${labelColor}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${bubble}`}>
                {i + 1}
              </span>
              <span className="text-xs font-medium hidden sm:inline">{labels[i]}</span>
            </div>
            {i < total - 1 && <div className={`h-0.5 flex-1 rounded ${connector}`} />}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Row — Zeile im Bestaetigungs-/Detail-Block: Label links (gedaempft), Wert rechts (hervorgehoben).
 * Vorher byte-identisch in NewOrderWizard und NewNotificationWizard dupliziert.
 * @param {string} label
 * @param {React.ReactNode} value
 */
export function Row({ label, value }) {
  const { isDarkMode } = useTheme();
  return (
    <div className="flex justify-between gap-3">
      <span className={isDarkMode ? 'text-white/60' : 'text-slate-500'}>{label}</span>
      <span className={`font-medium text-right ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value}</span>
    </div>
  );
}

/**
 * StatusBadge — farbiger SAP-Status-Chip.
 * Im Prototyp lag dieser in components/OrdersList.jsx und wurde auch von OrderDetail
 * importiert. Hier wandert er als geteiltes Primitiv ins UI-Kit.
 * Uebersetzung wird ueber 'pm.status.<code>' gesucht; fehlt sie, wird der rohe Code gezeigt.
 * @param {string} status — SAP-Statuscode (z. B. 'CRTD'|'REL'|'CNF'|'TECO')
 */
export function StatusBadge({ status }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Status -> Akzentfarbe. Unbekannte Codes fallen auf neutral zurueck.
  const palette = {
    CRTD: isDarkMode
      ? 'bg-amber-500/10 text-amber-300 border border-amber-400/20'
      : 'bg-amber-50 text-amber-700 border border-amber-200',
    REL: isDarkMode
      ? 'bg-brand/[0.12] text-blue-200 border border-brand/40'
      : 'bg-brand/[0.08] text-brand border border-brand/20',
    CNF: isDarkMode
      ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-400/20'
      : 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    TECO: isDarkMode
      ? 'bg-white/[0.08] text-white/60 border border-white/10'
      : 'bg-slate-100 text-slate-600 border border-slate-200',
  };
  const neutral = isDarkMode
    ? 'bg-white/[0.08] text-white/60 border border-white/10'
    : 'bg-slate-100 text-slate-600 border border-slate-200';

  const key = `pm.status.${status}`;
  const label = t(key);
  // t() faellt auf den Key selbst zurueck, wenn keine Uebersetzung existiert -> rohen Code zeigen.
  const text = label === key ? status : label;

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${palette[status] || neutral}`}>
      {text}
    </span>
  );
}
